import { Add, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjectAIConfig, toggleProjectAIConfig, createAIConfig, updateProjectAIConfig, deleteAIConfig } from '../../modules/aiConfig';
import AIKeyTable from './AiKeyTable';
import KeyModal from './KeyModal';
import AiUsageStats from './AIUsageStats';

const ProjectAIConfiguration = ({ user, projectId }) => {
  const isAdmin = ['org-admin', 'org-manager', 'admin'].includes(user?.role);
  const isOrgAdmin = ['org-admin', 'org-manager'].includes(user?.role);
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [orgAiEnabled, setOrgAiEnabled] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getProjectAIConfig(projectId);
      setOrgAiEnabled(!!res.org_ai_enabled);
      setConfigured(!!res.configured);
      setConfig(res.config);
      setIsEnabled(!!res.config?.is_enabled);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, [projectId]);

  const handleToggle = async (checked) => {
    setToggling(true);
    try {
      await toggleProjectAIConfig(projectId, checked);
      setIsEnabled(checked);
    } catch {
      // stays in previous state
    } finally {
      setToggling(false);
    }
  };

  const openAdd = () => { setEditEntry(null); setModalOpen(true); };
  const openEdit = (entry) => { setEditEntry(entry); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditEntry(null); };
  const createFn = (body) => createAIConfig({ ...body, applyTo: 'projects', projectIds: [projectId] });
  const updateFn = (body) => updateProjectAIConfig(projectId, body);

  const handleSaveEntry = () => {
    fetchConfig();
  };

  const handleDelete = async () => {
    try {
      await deleteAIConfig([projectId]);
      setConfig(null);
      setConfigured(false);
      setIsEnabled(false);
    } catch {
      // fail silently
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }


  const tableEntry = config ? {
    id: config.id,
    llm: config.provider ?? 'gemini',
    provider: config.provider ?? 'gemini',
    model: config.model ?? '',
    applyTo: 'projects',
    projectIds: [projectId],
    apiKey: config.api_key ?? '',
    dailyTokenLimit: config.daily_token_limit ?? null,
    monthlyTokenLimit: config.monthly_token_limit ?? null,
    addedBy: [config.creator?.first_name, config.creator?.last_name].filter(Boolean).join(' ') || '—',
    createdAt: config.created_at,
    features: (config.features ?? []).map((f) => ({
      featureKey: f.feature_key ?? f.featureKey,
      isEnabled: f.is_enabled ?? f.isEnabled ?? true,
      maxResults: f.max_results ?? f.maxResults ?? null,
      requestPerDay: f.request_per_day ?? f.requestPerDay ?? null,
    })),
  } : null;

  return (
    <>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>Configuration</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {toggling && <CircularProgress size={16} />}

              {isAdmin && configured && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => handleToggle(e.target.checked)}
                      disabled={toggling}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 0 }}
                />
              )}

              {isAdmin && orgAiEnabled && !configured && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Add sx={{ fontSize: 14 }} />}
                  onClick={openAdd}
                >
                  Add API Key
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure the AI API key and features for test case generation in this project.
          </Typography>

          {!orgAiEnabled && (
            isOrgAdmin ? (
              <Alert
                severity="info"
                action={
                  <Button size="small" endIcon={<OpenInNew sx={{ fontSize: 14 }} />} onClick={() => navigate('/settings')}>
                    Go to Settings
                  </Button>
                }
              >
                AI is not configured for this organisation. Enable AI configuration from the account settings.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                AI features are not enabled for this project. Please contact your org admin to enable AI configuration.
              </Alert>
            )
          )}

          {orgAiEnabled && !configured && !isAdmin && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              No AI API key is configured for this project. Contact your project admin to add an API key.
            </Alert>
          )}

          {configured && !isEnabled && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              AI features are disabled for this project. Enable the toggle to use AI features.
            </Alert>
          )}

          {configured && isEnabled && (
            <AIKeyTable
              entries={[tableEntry]}
              onEdit={isAdmin ? openEdit : undefined}
              onDelete={isAdmin ? handleDelete : undefined}
              showProject={false}
            />
          )}

        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}><CardContent sx={{ p: 3 }}><AiUsageStats projectId={projectId} /></CardContent></Card>
      <KeyModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSaveEntry}
        existingEntries={tableEntry ? [tableEntry] : []}
        editEntry={editEntry}
        showProject={false}
        createFn={createFn}
        updateFn={updateFn}
      />
    </>
  );
};

export default ProjectAIConfiguration;